/**
 * useReviewSessionPubSub — Real-time PubSub hook for Review Sessions.
 *
 * Connects to the Azure Web PubSub "ai_processing" hub, joins the
 * review's group (review_uuid), and dispatches Redux actions on incoming
 * finding/document/session events — providing live streaming of results.
 *
 * Events handled:
 *   review.finding_completed   → REVIEW_FINDING_RECEIVED
 *   review.document_completed  → REVIEW_DOCUMENT_COMPLETED
 *   review.document_failed     → REVIEW_DOCUMENT_COMPLETED (with failed status)
 *   review.progress            → REVIEW_SESSION_PROGRESS
 *   review.completed           → REVIEW_REVIEW_COMPLETED + silent refetch
 */

import { useEffect, useRef, useCallback } from 'react';
import { useAppDispatch } from '../../store';
import { getMultiHubSocketService } from '../../utils/MultiHubSocketService';
import {
  REVIEW_FINDING_RECEIVED,
  REVIEW_DOCUMENT_COMPLETED,
  REVIEW_REVIEW_COMPLETED,
  REVIEW_SESSION_PROGRESS,
  REVIEW_FINDING_DECISION_RECEIVED,
  REVIEW_SESSION_STATUS_CHANGE,
  fetchReviewSession,
  fetchFindings,
  fetchSignOffStatus,
} from '../../store/actions/Review/ReviewActions';

const HUB = 'ai_processing';

/**
 * PubSub event payloads emitted by the AI engine review handler.
 */
interface FindingCompletedPayload {
  review_uuid: string;
  finding: {
    uuid: string;
    severity: string;
    clause_type: string;
    section_reference: string | null;
    original_text: string | null;
    issue_description: string | null;
    suggested_redline: string | null;
    legal_basis: any;
    risk_score: number | null;
    confidence_score: number | null;
    document_name: string | null;
  };
}

interface DocumentCompletedPayload {
  review_uuid: string;
  document_id: number;
  file_name: string;
  status: 'completed' | 'failed';
  error: string | null;
}

interface ProgressPayload {
  review_uuid: string;
  documents_completed: number;
  documents_total: number;
  findings_count: number;
  progress_percent: number;
}

interface ReviewCompletedPayload {
  review_uuid: string;
  status: 'completed' | 'completed_with_errors';
  total_documents: number;
  failed_documents: number;
  total_findings: number;
  overall_risk_score: number | null;
  critical_count?: number;
  major_count?: number;
  minor_count?: number;
  info_count?: number;
}

/**
 * Hook: subscribe to real-time review processing events via PubSub.
 *
 * @param reviewUuid   The review UUID to subscribe to (also the PubSub group).
 * @param playbookUuid The parent playbook UUID (needed for refetch on completion).
 * @param enabled      Whether to connect (always true for collaboration, or when processing).
 */
export function useReviewSessionPubSub(
  reviewUuid: string | undefined,
  playbookUuid: string | undefined,
  enabled: boolean = true,
) {
  const dispatch = useAppDispatch();
  const handlerRef = useRef<((...args: any[]) => void) | null>(null);
  const groupRef = useRef<string | null>(null);
  const connectedRef = useRef(false);

  // ── Event handler (stable via ref) ──────────────────────────────────
  // IMPORTANT: MultiHubSocketService wildcard ('*') callback signature is:
  //   callback(...eventArgs, eventName)
  // i.e. the event name is the LAST argument, data is first.
  const handleEvent = useCallback(
    (data: any, eventName: string) => {
      if (!data || typeof data !== 'object') return;

      // The actual payload is nested inside `data.data` from the PubSub envelope
      const payload = data.data || data;

      switch (eventName) {
        case 'review.finding_completed': {
          const findingData = payload as FindingCompletedPayload;
          if (findingData.finding) {
            dispatch({
              type: REVIEW_FINDING_RECEIVED,
              payload: {
                ...findingData.finding,
                decision: null,
              },
            });
          }
          break;
        }

        case 'review.document_completed':
        case 'review.document_failed': {
          const docData = payload as DocumentCompletedPayload;
          dispatch({
            type: REVIEW_DOCUMENT_COMPLETED,
            payload: {
              document_id: docData.document_id,
              status: docData.status,
              error: docData.error ?? null,
            },
          });
          break;
        }

        case 'review.progress': {
          const progressData = payload as ProgressPayload;
          dispatch({
            type: REVIEW_SESSION_PROGRESS,
            payload: {
              documents_completed: progressData.documents_completed,
              documents_total: progressData.documents_total,
              findings_count: progressData.findings_count,
              progress_percent: progressData.progress_percent,
            },
          });
          break;
        }

        case 'review.completed': {
          const completedData = payload as ReviewCompletedPayload;
          dispatch({
            type: REVIEW_REVIEW_COMPLETED,
            payload: {
              status: completedData.status,
              overall_risk_score: completedData.overall_risk_score,
              findings_count: completedData.total_findings,
              critical_count: completedData.critical_count,
              major_count: completedData.major_count,
              minor_count: completedData.minor_count,
              info_count: completedData.info_count,
            },
          });

          // Silent refetch to sync full state from server
          if (reviewUuid) {
            dispatch(fetchReviewSession(reviewUuid, reviewUuid) as any);
            dispatch(fetchFindings(reviewUuid, reviewUuid, { page: 1, page_size: 200 }) as any);
          }
          break;
        }

        // Live collaboration: another user made a finding decision
        case 'finding_decision': {
          const decisionData = payload as {
            finding_uuid: string;
            decision: string;
            decision_by: string;
            decision_by_id: string;
            decision_at: string;
            overall_risk_score: number | null;
            modified_text?: string;
            severity?: string;
          };
          dispatch({
            type: REVIEW_FINDING_DECISION_RECEIVED,
            payload: decisionData,
          });
          break;
        }

        // Live collaboration: collaborator added/updated/removed
        case 'collaborator_added':
        case 'collaborator_updated':
        case 'collaborator_removed': {
          // Refetch session to get updated collaborators
          if (reviewUuid) {
            dispatch(fetchReviewSession(reviewUuid, reviewUuid) as any);
          }
          break;
        }

        // Live: session renamed/deleted by another collaborator
        case 'review_renamed':
        case 'review_deleted': {
          if (reviewUuid) {
            dispatch(fetchReviewSession(reviewUuid, reviewUuid) as any);
          }
          break;
        }

        // Live: status changed (e.g. pending_sign_off, finalized, reopened)
        case 'status_change': {
          const statusData = payload as { status: string; finalized_at?: string };
          dispatch({
            type: REVIEW_SESSION_STATUS_CHANGE,
            payload: statusData,
          });
          // Refresh sign-off status if we're in sign-off or finalized state
          if (reviewUuid && ['pending_sign_off', 'finalized'].includes(statusData.status)) {
            dispatch(fetchSignOffStatus(reviewUuid) as any);
          }
          break;
        }

        // Live: another user signed off
        case 'sign_off': {
          if (reviewUuid) {
            dispatch(fetchSignOffStatus(reviewUuid) as any);
          }
          break;
        }

        default:
          // Ignore other events on this hub
          break;
      }
    },
    [dispatch, reviewUuid],
  );

  // ── Connect / disconnect lifecycle ──────────────────────────────────
  useEffect(() => {
    if (!reviewUuid || !enabled) return;

    let cancelled = false;
    const service = getMultiHubSocketService();

    const setup = async () => {
      try {
        const connected = await service.connectToHub(HUB);
        if (cancelled || !connected) return;

        connectedRef.current = true;

        // Join the review's group (review handler emits to review_uuid)
        service.emit(HUB, 'join', { group: reviewUuid });
        groupRef.current = reviewUuid;

        // Register wildcard listener — callback receives (data, eventName) from MultiHubSocketService
        const handler = (data: any, eventName: string) => handleEvent(data, eventName);
        handlerRef.current = handler;
        service.on(HUB, '*', handler);
      } catch (err) {
        console.error('[useReviewSessionPubSub] Failed to connect:', err);
      }
    };

    setup();

    // ── Cleanup ──────────────────────────────────────────────────────
    return () => {
      cancelled = true;

      try {
        const svc = getMultiHubSocketService();

        // Leave group
        if (groupRef.current) {
          svc.emit(HUB, 'leave', { group: groupRef.current });
          groupRef.current = null;
        }

        // Remove listener
        if (handlerRef.current) {
          svc.off(HUB, '*', handlerRef.current);
          handlerRef.current = null;
        }

        connectedRef.current = false;
      } catch {
        // Best-effort cleanup
      }
    };
  }, [reviewUuid, enabled, handleEvent]);
}

export default useReviewSessionPubSub;
